import {
	all,
	call,
	put,
	takeEvery,
	takeLatest,
	select,
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { IRequestParams, request } from 'helpers/request';
import {
	getAdminEndpoint,
	getEndpoint,
	getEndpointWithData,
	requestSagaFactory,
} from 'helpers';
import { logSagaErrorMessage } from 'utils';
import { RoleTypes } from 'models';
import { roleSelector } from 'modules/admin-panel/selectors';
import * as types from './ActionTypes';
import {
	createNewUser,
	deletePartner,
	getPartnerAdmins,
	getPartnerClients,
	getPartnerData,
	getPartnerDetails,
	getPartnerList,
	postNewPartner,
	updatePartnerData,
	updatePartnerList,
} from './partners.actions';

import {
	IPartnerData,
	IPartnerDataPayload,
	IPartnerFormData,
	IPartnerList,
	ICreateNewUser,
} from './models';
import { mapPartnerPayload, mapUserPayloadToData } from './helpers';
import { ISearchPayload } from '../../models';
import { getClientUsersSaga } from '../clients/clients.saga';
import { tablePaginationDefaultValues } from '../../../../utils/constants/common';
import { AppPathList } from '../../../../router/AppPathList';
import { IInitialState } from '../../../../store/models';
import { getUserDetails } from '../settings/settings.actions';
import * as CONSTANTS from '../../../../utils/constants/common';
import { ISettingsPayload } from '../settings/models';

const baseUrl = getEndpoint(AppPathList.partners);
const baseUrlClients = getEndpoint(AppPathList.clients);
const baseUrlUsers = getAdminEndpoint('/users');

const ENDPOINTS = {
	partners: `${baseUrl}`,
	partnerList: `${baseUrl}/all`,
	partnerListShort: `${baseUrl}/all/short`,
	partnerData: `${baseUrl}/{{partnerId}}`,
	updatePartnerData: getEndpointWithData(
		`${AppPathList.partners}/:replacePartnerId`,
	),
	updatePartnerDataGeneralInfo: getEndpointWithData(
		`${AppPathList.partners}/general-info/:replacePartnerId`,
	),
	partnerAdminList: `${baseUrl}/{{partnerId}}/admins`,
	partnerClientList: `${baseUrlClients}/all`,
	newUser: baseUrlUsers,
};

function* getPartnerListSaga({ payload }: { payload: ISearchPayload }) {
	const queryString = {
		page: payload?.page || 0,
		size: payload?.size || 30,
		formalName: payload?.formalName || '',
		partnerId: payload?.partnerId,
	};

	try {
		yield put(getPartnerList.loading());

		const getPartnerListParams: IRequestParams = {
			method: 'GET',
			url: payload?.short ? ENDPOINTS.partnerListShort : ENDPOINTS.partnerList,
			params: queryString,
		};

		const { data }: AxiosResponse<{ id: number }> = yield call(
			request,
			getPartnerListParams,
		);

		yield put(
			getPartnerList.success(payload?.short ? { content: data } : data),
		);
	} catch (e) {
		logSagaErrorMessage('getPartnerList', e);
		yield put(getPartnerList.fail(e));
	}
}

const updatePartnerListSaga = ({ payload }: { payload?: ISearchPayload }) => {
	const queryString = {
		page: payload?.page || 0,
		size: payload?.size || 30,
		formalName: payload?.formalName || '',
	};

	return requestSagaFactory<ISearchPayload, IPartnerList>({
		actionApi: updatePartnerList,
		url: ENDPOINTS.partnerList,
		logErrorMessage: 'updatePartnerList',
	})({ payload: queryString });
};

function* postNewPartnerSaga({ payload }: { payload: IPartnerFormData }) {
	try {
		yield put(postNewPartner.loading());

		const postNewPartnerData: IPartnerDataPayload = mapPartnerPayload(payload);

		const postNewPartnerParams: IRequestParams = {
			method: 'POST',
			url: ENDPOINTS.partners,
			data: postNewPartnerData,
		};

		const { data }: AxiosResponse<{ id: number }> = yield call(
			request,
			postNewPartnerParams,
		);

		yield put(postNewPartner.success(data));
	} catch (e) {
		logSagaErrorMessage('postNewPartner', e);
		yield put(postNewPartner.fail(e));
	}
}

function* updatePartnerDataSaga({ payload }: { payload: IPartnerFormData }) {
	try {
		yield put(updatePartnerData.loading());
		const role = yield select(roleSelector);

		const updatePartnerDataParams = mapPartnerPayload(payload);

		const postNewClientParams: IRequestParams = {
			method: 'PUT',
			url:
				role === RoleTypes.SUPER_ADMIN
					? ENDPOINTS.updatePartnerData(payload.partnerId)
					: ENDPOINTS.updatePartnerDataGeneralInfo(payload.partnerId),
			data: updatePartnerDataParams,
		};

		const { data }: AxiosResponse<{ id: string }> = yield call(
			request,
			postNewClientParams,
		);

		yield put(updatePartnerData.success(data));

		const currentPartnerId = yield select(
			(state: IInitialState) => state.admin.auth.partnerId,
		);
		if (currentPartnerId === payload.partnerId) {
			yield call(getUserDetailsSaga);
		}
	} catch (e) {
		logSagaErrorMessage('updateClientDetails', e);
		yield put(updatePartnerData.fail(e));
	}
}

export const getPartnerAdminsSaga = requestSagaFactory({
	url: ENDPOINTS.partnerAdminList,
	actionApi: getPartnerAdmins,
	logErrorMessage: 'Get partner admins',
});

const getPartnerClientsSaga = requestSagaFactory({
	url: ENDPOINTS.partnerClientList,
	actionApi: getPartnerClients,
	logErrorMessage: 'Get partner clients',
});

const getPartnerDataSaga = requestSagaFactory<
	{ partnerId: string },
	IPartnerData
>({
	url: ENDPOINTS.partnerData,
	actionApi: getPartnerData,
	logErrorMessage: 'getPartnerData',
});

function* getPartnerDetailsSaga(action: { payload: { partnerId: string } }) {
	yield put(getPartnerDetails.loading());
	yield all([
		call(getPartnerListSaga, action),
		call(getPartnerAdminsSaga, action),
		call(getPartnerClientsSaga, {
			...action,
			payload: {
				...action.payload,
				...tablePaginationDefaultValues,
			},
		}),
	]);
	yield put(getPartnerDetails.success());
}

export function* postNewUserSaga({ payload }: { payload: ICreateNewUser }) {
	try {
		yield put(createNewUser.loading());

		const requestData = mapUserPayloadToData(payload);

		const postNewUserParams: IRequestParams = {
			method: 'POST',
			url: ENDPOINTS.newUser,
			data: requestData,
		};

		const { data }: AxiosResponse<{ id: number }> = yield call(
			request,
			postNewUserParams,
		);

		yield put(createNewUser.success(data));
		return true;
	} catch (e) {
		logSagaErrorMessage('createNewUser', e);
		yield put(createNewUser.fail(e));
		return false;
	}
}

function* createNewAdminSaga({ payload }: { payload: ICreateNewUser }) {
	const isCreated = yield call(postNewUserSaga, { payload });
	if (!isCreated) return;
	if (payload.clientId)
		yield call(getClientUsersSaga, { payload: payload.clientId });
	if (payload.partnerId && !payload.clientId)
		yield call(getPartnerAdminsSaga, {
			payload: { partnerId: payload.partnerId },
		});
}

function* getUserDetailsSaga() {
	try {
		yield put(getUserDetails.loading());
		const userDetailsParams: IRequestParams = {
			url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/users/current/details`,
		};

		const { data }: AxiosResponse<ISettingsPayload> = yield call(
			request,
			userDetailsParams,
		);

		yield put(getUserDetails.success(data));
	} catch (e) {
		logSagaErrorMessage('getUserDetails', e);
		yield put(getUserDetails.fail(e));
	}
}

function* deletePartnerSaga({ payload }: { payload: { partnerId: string } }) {
	try {
		yield put(deletePartner.loading());

		const deletePartnerParams: IRequestParams = {
			method: 'DELETE',
			url: `${ENDPOINTS.partners}/${payload.partnerId}`,
		};

		yield call(request, deletePartnerParams);

		yield put(deletePartner.success());
	} catch (e) {
		logSagaErrorMessage('deletePartnerSaga', e);
		yield put(deletePartner.fail(e));
	}
}

export function* partnersSaga() {
	yield all([
		// @ts-ignore
		takeLatest(types.GET_PARTNER_LIST.DEFAULT, getPartnerListSaga),
		// @ts-ignore
		takeLatest(types.UPDATE_PARTNER_LIST.DEFAULT, updatePartnerListSaga),
		// @ts-ignore
		takeEvery(types.POST_NEW_PARTNER.DEFAULT, postNewPartnerSaga),
		// @ts-ignore
		takeEvery(types.POST_NEW_USER.DEFAULT, createNewAdminSaga),
		// @ts-ignore
		takeLatest(types.GET_PARTNER_DETAILS.DEFAULT, getPartnerDetailsSaga),
		// @ts-ignore
		takeLatest(types.GET_PARTNER_DATA.DEFAULT, getPartnerDataSaga),
		// @ts-ignore
		takeLatest(types.GET_PARTNER_ADMINS.DEFAULT, getPartnerAdminsSaga),
		// @ts-ignore
		takeLatest(types.GET_PARTNER_CLIENTS.DEFAULT, getPartnerClientsSaga),
		// @ts-ignore
		takeLatest(types.UPDATE_PARTNER_DATA.DEFAULT, updatePartnerDataSaga),
		// @ts-ignore
		takeLatest(types.DELETE_PARTNER.DEFAULT, deletePartnerSaga),
	]);
}
